import { DropdownMenu as MenuPrimitive, Popover as PopoverPrimitive } from 'radix-ui';
import { Loader2 } from 'lucide-react';
import { cn } from '../../lib/cn';
import { Icon, type LucideIcon } from './icon';
import { Kbd } from './kbd';

const schwebend = 'z-[2000] rounded-lg border border-border bg-popover text-popover-foreground shadow-md animate-ek-pop';

export const DropdownMenu = MenuPrimitive.Root;
export const DropdownMenuTrigger = MenuPrimitive.Trigger;
export const DropdownMenuGroup = MenuPrimitive.Group;

export function DropdownMenuContent({ className, sideOffset = 6, align = 'end', ...rest }: MenuPrimitive.DropdownMenuContentProps) {
  return (
    <MenuPrimitive.Portal>
      <MenuPrimitive.Content sideOffset={sideOffset} align={align} className={cn(schwebend, 'min-w-44 p-1', className)} {...rest} />
    </MenuPrimitive.Portal>
  );
}

export interface DropdownMenuItemProps extends MenuPrimitive.DropdownMenuItemProps {
  icon?: LucideIcon;
  kbd?: string;
  /** Löschen und Unumkehrbares in danger. */
  tone?: 'default' | 'danger';
  /** Läuft: Spinner statt Icon, gesperrt. Wie `Button loading`. */
  loading?: boolean;
}

export function DropdownMenuItem({ icon, kbd, tone = 'default', loading, disabled, className, children, ...rest }: DropdownMenuItemProps) {
  return (
    <MenuPrimitive.Item
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        'flex h-8 cursor-default select-none items-center gap-2 rounded-sm px-2 text-sm outline-none touch:h-11',
        'data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-45',
        tone === 'danger' && 'text-danger data-[highlighted]:text-danger',
        className,
      )}
      {...rest}
    >
      {loading ? (
        <Icon icon={Loader2} className="animate-spin text-muted-foreground" />
      ) : (
        icon && <Icon icon={icon} className={tone === 'danger' ? undefined : 'text-muted-foreground'} />
      )}
      <span className="flex-1">{children}</span>
      {kbd && <Kbd>{kbd}</Kbd>}
    </MenuPrimitive.Item>
  );
}

export function DropdownMenuLabel({ className, ...rest }: MenuPrimitive.DropdownMenuLabelProps) {
  return <MenuPrimitive.Label className={cn('px-2 pb-1 pt-1.5 t-label text-muted-foreground', className)} {...rest} />;
}

export function DropdownMenuSeparator({ className, ...rest }: MenuPrimitive.DropdownMenuSeparatorProps) {
  return <MenuPrimitive.Separator className={cn('-mx-1 my-1 h-px bg-border', className)} {...rest} />;
}

export const Popover = PopoverPrimitive.Root;
export const PopoverTrigger = PopoverPrimitive.Trigger;
export const PopoverAnchor = PopoverPrimitive.Anchor;

export function PopoverContent({ className, sideOffset = 6, align = 'start', ...rest }: PopoverPrimitive.PopoverContentProps) {
  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content sideOffset={sideOffset} align={align} className={cn(schwebend, 'p-3 outline-none', className)} {...rest} />
    </PopoverPrimitive.Portal>
  );
}
